import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Breadcrumbs,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Link,
  LinearProgress,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  ArrowUpward as UpIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CloudDownload as CloudDownloadIcon,
  Lock as LockIcon,
  Photo as PhotoIcon,
  VideoLibrary as VideoIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Storage as StorageIcon,
  Layers as LayersIcon,
  Image as ImagePlaceholderIcon,
  Cloud as CloudIcon,
  CloudOff as CloudOffIcon,
  Computer as ServerIcon,
  FolderShared as MountIcon
} from '@mui/icons-material';
import { Dialog, DialogContent, DialogTitle, Fade, Zoom } from '@mui/material';
import { StatusChipBase, getStatusChipStyles } from '../styles/sharedStyles';

// Format bytes to human readable
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Root path for the file explorer (locked)
const ROOT_PATH = '/home/pp/Immich_Adam';

const IMAGE_PREVIEW_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];
const MAX_PREVIEW_SIZE = 50 * 1024 * 1024; // Increase to 50MB for NFS speed

function FileExplorer({ apiBaseUrl, token }) {
  const [currentPath, setCurrentPath] = useState(ROOT_PATH);
  const [entries, setEntries] = useState([]);
  const [libraryInfo, setLibraryInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [zipJob, setZipJob] = useState(null); // { id, status, scannedFiles, totalSize, ready }
  const [nfsStatus, setNfsStatus] = useState({ isMounted: null, loading: true });
  
  // Bulk download dialog state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkStartId, setBulkStartId] = useState(1);
  const [bulkEndId, setBulkEndId] = useState(10);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkError, setBulkError] = useState(null);

  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [previewCache, setPreviewCache] = useState({});
  const [previewLoading, setPreviewLoading] = useState({});
  const [fullPreviewEntry, setFullPreviewEntry] = useState(null);
  const [fullPreviewUrl, setFullPreviewUrl] = useState(null);
  const [fullPreviewLoading, setFullPreviewLoading] = useState(false);
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [recentPhotosLoading, setRecentPhotosLoading] = useState(false);
  const previewUrlsRef = useRef([]);

  const fetchDirectory = useCallback(async (path) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/truenas/filesystem/list`, {
        params: { path },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setEntries(response.data.entries || []);
        if (response.data.library_info) {
          setLibraryInfo(response.data.library_info);
        }
      } else {
        setError(response.data.error || 'Failed to list directory');
      }
    } catch (err) {
      console.error('Error fetching directory:', err);
      setError(err.response?.data?.error || 'Failed to fetch directory contents');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  const fetchLibraryInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/truenas/filesystem/library-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLibraryInfo(response.data);
    } catch (err) {
      console.error('Error fetching library info:', err);
    }
  }, [apiBaseUrl, token]);

  const fetchNfsStatus = useCallback(async () => {
    try {
      setNfsStatus(prev => ({ ...prev, loading: true }));
      const response = await axios.get(`${apiBaseUrl}/api/nfs/mounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const mounts = response.data.mounts || [];
      const targetMount = mounts.find(mount => mount.mount_point === ROOT_PATH);
      setNfsStatus({
        isMounted: targetMount ? targetMount.is_mounted : false,
        loading: false
      });
    } catch (err) {
      console.error('Error fetching NFS mount status:', err);
      setNfsStatus({
        isMounted: false,
        loading: false
      });
    }
  }, [apiBaseUrl, token]);

  const fetchRecentPhotos = useCallback(async () => {
    try {
      setRecentPhotosLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/truenas/filesystem/recent-photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.photos) {
        setRecentPhotos(response.data.photos);
      }
    } catch (err) {
      console.error('Error fetching recent photos:', err);
    } finally {
      setRecentPhotosLoading(false);
    }
  }, [apiBaseUrl, token]);

  // Auto-load recent photo thumbnails when photos are loaded
  useEffect(() => {
    if (recentPhotos.length > 0) {
      recentPhotos.forEach((photo) => {
        const photoEntry = { type: 'file', name: photo.name, path: photo.path };
        if (isPreviewable(photoEntry)) {
          ensurePreview(photoEntry);
        }
      });
    }
  }, [recentPhotos.length]); // Only trigger when count changes

  // Effect to re-fetch directory contents when mount status changes from unmounted to mounted
  const [prevMountStatus, setPrevMountStatus] = useState(null);
  useEffect(() => {
    if (prevMountStatus === false && nfsStatus.isMounted === true) {
      console.log('NFS mount detected, re-fetching directory contents...');
      fetchDirectory(currentPath);
      if (currentPath === ROOT_PATH) {
        fetchLibraryInfo();
        fetchRecentPhotos();
      }
    }
    setPrevMountStatus(nfsStatus.isMounted);
  }, [nfsStatus.isMounted, currentPath, fetchDirectory, fetchLibraryInfo, fetchRecentPhotos]);

  useEffect(() => {
    if (currentPath === ROOT_PATH && !libraryInfo) {
      fetchLibraryInfo();
    }
    fetchRecentPhotos();
    fetchNfsStatus();
    // NO AUTO-REFRESH for NFS status to prevent HDD spin-up
    // Status is only checked when component mounts or user manually refreshes
  }, [currentPath, libraryInfo, fetchLibraryInfo, fetchRecentPhotos, fetchNfsStatus]);

  // Derived sorted entries with folder IDs (only at root level)
  const sortedEntriesWithIds = React.useMemo(() => {
    const sorted = [...entries].sort((a, b) => {
      // Always keep directories at top
      if (a.type === 'directory' && b.type !== 'directory') return -1;
      if (a.type !== 'directory' && b.type === 'directory') return 1;
      
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (typeof aValue === 'string') {
        const cmp = aValue.localeCompare(bValue, undefined, { sensitivity: 'base', numeric: true });
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      }
      
      const cmp = (aValue || 0) - (bValue || 0);
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    // Assign IDs to directories at root level (sorted by folder name as year-month)
    // Oldest folder name = ID 1, newest = highest ID
    // IDs are stable - new folders get next available ID based on name sort
    if (currentPath === ROOT_PATH) {
      const directories = sorted.filter(e => e.type === 'directory');
      
      // Helper to parse folder name as year-month for sorting
      const parseFolderName = (name) => {
        const parts = name.split('-');
        if (parts.length === 2) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
            return year * 12 + month; // Convert to sortable number
          }
        }
        return Infinity; // Folders that don't match the pattern go to the end
      };
      
      // Sort directories by folder name (year-month ascending = oldest first)
      const dirsByName = [...directories].sort((a, b) => {
        const aScore = parseFolderName(a.name);
        const bScore = parseFolderName(b.name);
        return aScore - bScore;
      });
      
      // Create ID map (oldest = 1, newest = highest)
      const idMap = {};
      dirsByName.forEach((dir, index) => {
        idMap[dir.path] = index + 1;
      });
      
      // Assign IDs to all entries
      return sorted.map(entry => ({
        ...entry,
        folderId: entry.type === 'directory' ? idMap[entry.path] : null
      }));
    }
    
    return sorted;
  }, [entries, sortConfig, currentPath]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      previewUrlsRef.current = [];
    };
  }, []);

  useEffect(() => {
    previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    setPreviewCache({});
    setPreviewLoading({});
  }, [currentPath]);

  useEffect(() => {
    fetchDirectory(currentPath);
  }, [currentPath, fetchDirectory]);

  const handleNavigate = (entry) => {
    if (entry.type === 'directory') {
      setCurrentPath(entry.path);
    }
  };

  const handleRefresh = () => {
    fetchDirectory(currentPath);
    if (currentPath === ROOT_PATH) fetchLibraryInfo();
  };

  const isPreviewable = useCallback((entry) => {
    if (!entry || entry.type !== 'file') return false;
    const name = entry.name?.toLowerCase() || '';
    if (!IMAGE_PREVIEW_EXTENSIONS.some(ext => name.endsWith(ext))) return false;
    // We can handle larger images now with NFS
    return true;
  }, []);

  const isVideo = useCallback((entry) => {
    if (!entry || entry.type !== 'file') return false;
    const name = entry.name?.toLowerCase() || '';
    return VIDEO_EXTENSIONS.some(ext => name.endsWith(ext));
  }, []);

  const ensurePreview = useCallback(async (entry) => {
    if (!entry || !isPreviewable(entry)) return;
    const existing = previewCache[entry.path];
    if (existing && existing !== '__error') return;
    if (previewLoading[entry.path]) return;

    setPreviewLoading(prev => ({ ...prev, [entry.path]: true }));
    try {
      const response = await axios.get(`${apiBaseUrl}/api/truenas/filesystem/preview`, {
        params: { path: entry.path },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error('Preview response is not a Blob');
      }

      const url = URL.createObjectURL(blob);
      previewUrlsRef.current.push(url);
      setPreviewCache(prev => ({ ...prev, [entry.path]: url }));
    } catch (err) {
      console.error('Error fetching preview:', err);
      setPreviewCache(prev => ({ ...prev, [entry.path]: '__error' }));
    } finally {
      setPreviewLoading(prev => ({ ...prev, [entry.path]: false }));
    }
  }, [apiBaseUrl, token, isPreviewable, previewCache, previewLoading]);

  const handleFullPreview = async (entry) => {
    if (!isPreviewable(entry)) return;
    setFullPreviewEntry(entry);
    setFullPreviewLoading(true);
    setFullPreviewUrl(null);
    
    try {
      const response = await axios.get(`${apiBaseUrl}/api/truenas/filesystem/preview-full`, {
        params: { path: entry.path },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(response.data);
      setFullPreviewUrl(url);
    } catch (err) {
      console.error('Error fetching full preview:', err);
      setError('Failed to load high-quality preview');
    } finally {
      setFullPreviewLoading(false);
    }
  };

  const closeFullPreview = () => {
    if (fullPreviewUrl) URL.revokeObjectURL(fullPreviewUrl);
    setFullPreviewEntry(null);
    setFullPreviewUrl(null);
  };

  const previewLoadedRef = useRef(new Set());

  const handleDownloadFile = async (entry) => {
    if (entry.type !== 'file') return;
    
    setDownloading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/truenas/filesystem/download`, {
        params: { path: entry.path },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', entry.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    } finally {
      setDownloading(false);
    }
  };

  const pollZipProgress = useCallback(async (jobId) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/truenas/filesystem/download-zip/progress/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { status, scanned_files, total_size, ready } = response.data;
      setZipJob({ id: jobId, status, scannedFiles: scanned_files, totalSize: total_size, ready });
      
      if (ready) {
        setDownloading(false);
        // Trigger direct browser download
        const downloadUrl = `${apiBaseUrl}/api/truenas/filesystem/download-zip/stream/${jobId}?token=${token}`;
        window.location.assign(downloadUrl);
        // Reset job after a delay
        setTimeout(() => setZipJob(null), 10000);
      } else if (status === 'error') {
        setError('Failed to prepare ZIP: ' + response.data.error);
        setDownloading(false);
        setZipJob(null);
      } else {
        // Continue polling
        setTimeout(() => pollZipProgress(jobId), 1000);
      }
    } catch (err) {
      console.error('Error polling ZIP progress:', err);
      setDownloading(false);
      setZipJob(null);
    }
  }, [apiBaseUrl, token]);

  const startZipJob = async (path) => {
    setDownloading(true);
    setError(null);
    setZipJob({ status: 'initializing', scannedFiles: 0, totalSize: 0, ready: false });
    
    try {
      const response = await axios.post(`${apiBaseUrl}/api/truenas/filesystem/download-zip/prepare`, null, {
        params: { path },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const jobId = response.data.job_id;
      pollZipProgress(jobId);
    } catch (err) {
      console.error('Error starting ZIP job:', err);
      setError('Failed to start ZIP preparation');
      setDownloading(false);
      setZipJob(null);
    }
  };

  const handleDownloadFolder = async (entry) => {
    startZipJob(entry.path);
  };

  const handleDownloadLibrary = async () => {
    startZipJob(ROOT_PATH);
  };

  const handleBulkDownload = async () => {
    if (bulkStartId >= bulkEndId) {
      setBulkError('End ID must be greater than Start ID');
      return;
    }
    
    setBulkDownloading(true);
    setBulkError(null);
    
    try {
      const response = await axios.post(`${apiBaseUrl}/api/truenas/filesystem/download-bulk-zip/prepare`, null, {
        params: { start_id: parseInt(bulkStartId), end_id: parseInt(bulkEndId) },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const jobId = response.data.job_id;
      setBulkDialogOpen(false);
      
      // Build custom name for the ZIP file
      const customName = `download[${bulkStartId}][${bulkEndId}]immich-folders`;
      
      // Poll for progress and download
      const pollBulkProgress = async (currentJobId) => {
        try {
          const progressResponse = await axios.get(`${apiBaseUrl}/api/truenas/filesystem/download-zip/progress/${currentJobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const { status, scanned_files, total_size, ready } = progressResponse.data;
          
          if (ready) {
            setBulkDownloading(false);
            // Trigger download with custom name
            const downloadUrl = `${apiBaseUrl}/api/truenas/filesystem/download-zip/stream/${currentJobId}?token=${token}&custom_name=${encodeURIComponent(customName)}`;
            window.location.assign(downloadUrl);
          } else if (status === 'error') {
            setBulkDownloading(false);
            setBulkError('Failed to prepare ZIP: ' + progressResponse.data.error);
          } else {
            setTimeout(() => pollBulkProgress(currentJobId), 1000);
          }
        } catch (err) {
          console.error('Error polling bulk ZIP progress:', err);
          setBulkDownloading(false);
          setBulkError('Failed to prepare bulk download');
        }
      };
      
      pollBulkProgress(jobId);
    } catch (err) {
      console.error('Error starting bulk ZIP:', err);
      setBulkDownloading(false);
      setBulkError(err.response?.data?.detail || 'Failed to start bulk download');
    }
  };

  const openBulkDialog = () => {
    setBulkDialogOpen(true);
    setBulkError(null);
  };

  // Parse path into breadcrumbs
  const getBreadcrumbs = () => {
    if (!currentPath.startsWith(ROOT_PATH)) {
      return [{ label: 'Restricted', path: currentPath }];
    }
    
    const relativePath = currentPath.slice(ROOT_PATH.length);
    const parts = relativePath.split('/').filter(Boolean);
    
    const breadcrumbs = [{ label: '📁 Library', path: ROOT_PATH }];
    let currentPathStr = ROOT_PATH;
    
    parts.forEach(part => {
      currentPathStr += '/' + part;
      breadcrumbs.push({ label: part, path: currentPathStr });
    });
    
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();
  const isAtRoot = currentPath === ROOT_PATH;

  return (
    <Box>
      {/* Header with Storage Info */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src="/immich.png"
              alt="Immich"
              sx={{
                width: 48,
                height: 48,
                objectFit: 'contain',
                filter: 'drop-shadow(0 0 10px rgba(0, 212, 255, 0.5))'
              }}
            />
            <Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 0.5, letterSpacing: '-0.5px' }}>
                Adam Library <span style={{ color: '#00d4ff' }}>Explorer</span>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LockIcon sx={{ fontSize: 14, color: '#00ff88' }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
                  Read-Only Access | Powered by TrueNAS NFS | Last scanned: {isAtRoot && libraryInfo?.last_updated ? new Date(libraryInfo.last_updated).toLocaleTimeString('pl-PL', { timeZone: 'Europe/Warsaw' }) : 'N/A'}
                </Typography>
              </Box>
              
              {/* Mount Button */}
              {!nfsStatus.isMounted && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CloudIcon sx={{ fontSize: 14 }} />}
                  onClick={async () => {
                    try {
                      await axios.post(`${apiBaseUrl}/api/nfs/mount`, {
                        mount_point: ROOT_PATH
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      // Refresh NFS status
                      await fetchNfsStatus();
                    } catch (err) {
                      console.error('Error mounting NFS share:', err);
                      setError('Failed to mount NFS share');
                    }
                  }}
                  sx={{
                    backgroundColor: '#00d4ff',
                    color: '#000',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    py: 0.5,
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: '#00b3ff',
                    }
                  }}
                >
                  Mount
                </Button>
              )}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            {/* Vertical button stack */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<LayersIcon />}
                onClick={openBulkDialog}
                disabled={downloading || isAtRoot === false}
                sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  px: 2,
                  py: 0.75,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                  textTransform: 'none',
                  color: '#fff',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                  },
                  '&:disabled': {
                    background: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.3)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Bulk Download
              </Button>
              
              <Button
                variant="contained"
                startIcon={<CloudDownloadIcon />}
                onClick={handleDownloadLibrary}
                disabled={downloading || isAtRoot === false}
                sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  px: 2,
                  py: 0.75,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                  textTransform: 'none',
                  color: '#fff',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                  },
                  '&:disabled': {
                    background: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.3)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Full Library ZIP
              </Button>
            </Box>
            
            {/* Library Information Card - Top right */}
            {libraryInfo && (
              <Paper sx={{ 
                p: 2, 
                background: 'rgba(0, 212, 255, 0.08)', 
                border: '1px solid rgba(0, 212, 255, 0.2)',
                borderRadius: '12px',
                minWidth: 280,
                ml: 'auto'
              }}>
                <Typography variant="subtitle2" sx={{ color: '#00d4ff', mb: 1, fontWeight: 600 }}>
                  Library Information
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Total Size: {libraryInfo.total_size_human}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Chip 
                      size="small" 
                      label={`${libraryInfo.folder_count || 0} folders`}
                      sx={{ backgroundColor: 'rgba(0, 212, 255, 0.15)', color: '#fff', fontSize: '0.7rem' }}
                    />
                    <Chip 
                      size="small" 
                      label={`${libraryInfo.file_count || 0} files`}
                      sx={{ backgroundColor: 'rgba(0, 255, 136, 0.15)', color: '#fff', fontSize: '0.7rem' }}
                    />
                  </Box>
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>



      {/* Recently Added Photos */}
      {isAtRoot && recentPhotos.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhotoIcon sx={{ fontSize: 18, color: '#00d4ff' }} />
            Recently Added Photos
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(10, 1fr)',
            gap: 1,
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            p: 1.5
          }}>
            {recentPhotos.slice(0, 20).map((photo, index) => {
              const photoEntry = { type: 'file', name: photo.name, path: photo.path };
              const hasPreview = isPreviewable(photoEntry) && previewCache[photo.path] && previewCache[photo.path] !== '__error';
              const previewIsLoading = isPreviewable(photoEntry) && previewLoading[photo.path];
              
              return (
              <Box
                key={photo.id || index}
                onMouseEnter={() => isPreviewable(photoEntry) && ensurePreview(photoEntry)}
                sx={{
                  aspectRatio: '1',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer',
                  transform: 'scale(1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  background: hasPreview ? 'transparent' : 'rgba(255,255,255,0.03)',
                  '&:hover': {
                    transform: 'scale(1.15)',
                    zIndex: 10,
                    boxShadow: '0 8px 25px rgba(0, 212, 255, 0.4)',
                    borderRadius: '12px',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    transition: 'border-color 0.3s ease',
                  },
                  '&:hover::after': {
                    borderColor: 'rgba(0, 212, 255, 0.5)',
                  }
                }}
                onClick={() => {
                  handleFullPreview(photoEntry);
                }}
              >
                {hasPreview ? (
                  <Box
                    component="img"
                    src={previewCache[photo.path]}
                    alt={photo.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : previewIsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    <CircularProgress size={24} sx={{ color: '#00d4ff' }} />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    <ImagePlaceholderIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.2)' }} />
                  </Box>
                )}
              </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Download Progress */}
      {(downloading || zipJob) && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress 
            variant={zipJob?.ready ? "determinate" : "indeterminate"}
            value={zipJob?.ready ? 100 : undefined}
            sx={{ 
              borderRadius: '8px',
              height: 10,
              background: 'rgba(255,255,255,0.1)',
              '& .MuiLinearProgress-bar': {
                background: (theme) => theme.palette.primary.main,
                borderRadius: '8px',
              }
            }} 
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" sx={{ color: '#00d4ff', fontWeight: 700 }}>
              {zipJob?.ready ? 'STARTING DOWNLOAD...' : `PREPARING ZIP: Scanned ${zipJob?.scannedFiles || 0} files...`}
            </Typography>
            {zipJob?.totalSize > 0 && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
                Estimated Size: {formatBytes(zipJob.totalSize)}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Path bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <IconButton
            disabled={isAtRoot}
            onClick={() => {
              const parts = currentPath.split('/').filter(Boolean);
              parts.pop();
              setCurrentPath('/' + parts.join('/'));
            }}
            sx={{ color: isAtRoot ? 'rgba(255,255,255,0.1)' : '#00d4ff' }}
          >
            <UpIcon />
          </IconButton>

          <Breadcrumbs
            sx={{
              color: '#fff',
              '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.3)' },
              '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' }
            }}
          >
            {breadcrumbs.map((crumb, index) => (
              <Link
                key={crumb.path}
                component="button"
                variant="body2"
                onClick={() => crumb.path !== currentPath && setCurrentPath(crumb.path)}
                sx={{
                  color: index === breadcrumbs.length - 1 ? '#00d4ff' : 'rgba(255,255,255,0.6)',
                  fontWeight: index === breadcrumbs.length - 1 ? 700 : 400,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#fff',
                  }
                }}
              >
                {crumb.label}
              </Link>
            ))}
          </Breadcrumbs>

          <Tooltip title="Refresh Folder">
            <IconButton
              onClick={handleRefresh}
              sx={{ color: 'rgba(255,255,255,0.7)', ml: 'auto', '&:hover': { color: '#00d4ff' } }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Error alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            mb: 3,
            borderRadius: '12px',
            background: 'rgba(255,68,68,0.1)',
            border: '1px solid rgba(255,68,68,0.3)',
            '& .MuiAlert-message': { color: '#ff8888' }
          }}
        >
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#00d4ff' }} />
        </Box>
      )}

      {/* File table */}
      {!loading && !error && (
        <TableContainer
          component={Paper}
          sx={{
            background: 'rgba(255,255,255,0.02)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'rgba(255,255,255,0.03)' }}>
                <TableCell sx={{ color: 'rgba(255,255,255,0.4)', py: 2, width: 80, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}>Preview</TableCell>
                {currentPath === ROOT_PATH && (
                  <TableCell sx={{ color: 'rgba(255,255,255,0.4)', py: 2, width: 60, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', textAlign: 'center' }}>ID</TableCell>
                )}
                <TableCell 
                  onClick={() => handleSort('name')}
                  sx={{ 
                    color: 'rgba(255,255,255,0.4)', 
                    py: 2, 
                    cursor: 'pointer',
                    fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem',
                    '&:hover': { color: '#00d4ff' }
                  }}
                >
                  Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.4)', py: 2, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}>Type</TableCell>
                <TableCell 
                  onClick={() => handleSort('size')}
                  sx={{ 
                    color: 'rgba(255,255,255,0.4)', 
                    py: 2, 
                    cursor: 'pointer',
                    fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem',
                    '&:hover': { color: '#00d4ff' }
                  }}
                >
                  Size {sortConfig.key === 'size' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.4)', py: 2, textAlign: 'right', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEntriesWithIds.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={currentPath === ROOT_PATH ? 6 : 5} sx={{ color: 'rgba(255,255,255,0.3)', py: 10, textAlign: 'center' }}>
                    <FolderIcon sx={{ fontSize: 48, mb: 2, opacity: 0.2 }} />
                    <Typography variant="body1">This folder is empty</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedEntriesWithIds.map((entry, index) => {
                  const previewUrl = previewCache[entry.path];
                  const hasPreview = isPreviewable(entry) && previewUrl && previewUrl !== '__error';
                  const previewIsLoading = isPreviewable(entry) && previewLoading[entry.path];
                  const isVid = isVideo(entry);

                  return (
                    <TableRow
                      key={entry.path}
                      onMouseEnter={() => isPreviewable(entry) && ensurePreview(entry)}
                      sx={{
                        borderBottom: '1px solid rgba(255,255,255,0.03)',
                        animation: `fadeInUp 0.3s ease-out`,
                        animationDelay: `${Math.min(index * 0.03, 1)}s`,
                        animationFillMode: 'both',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.04)'
                        }
                      }}
                    >
                    <TableCell sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {entry.type === 'directory' ? (
                          <FolderIcon sx={{ color: '#ffd54f', fontSize: 32 }} />
                        ) : hasPreview ? (
                          <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={() => handleFullPreview(entry)}>
                            <Box
                              component="img"
                              src={previewUrl}
                              alt={entry.name}
                              sx={{
                                width: 50,
                                height: 50,
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '2px solid rgba(255,255,255,0.1)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  borderColor: '#00d4ff',
                                  boxShadow: '0 0 15px rgba(0, 212, 255, 0.4)'
                                }
                              }}
                            />
                            <Box sx={{ position: 'absolute', bottom: -4, right: -4, background: '#00d4ff', borderRadius: '50%', p: 0.2, display: 'flex' }}>
                              <ZoomInIcon sx={{ fontSize: 12, color: '#000' }} />
                            </Box>
                          </Box>
                        ) : previewIsLoading ? (
                          <CircularProgress size={24} sx={{ color: '#00d4ff' }} />
                        ) : isVid ? (
                          <VideoIcon sx={{ color: '#ff4081', fontSize: 32 }} />
                        ) : (
                          <FileIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 32 }} />
                        )}
                      </Box>
                    </TableCell>
                    {currentPath === ROOT_PATH && entry.type === 'directory' && (
                      <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#00d4ff',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            fontFamily: 'monospace',
                            background: 'rgba(0, 212, 255, 0.1)',
                            borderRadius: '8px',
                            px: 1.5,
                            py: 0.5,
                            display: 'inline-block',
                            minWidth: 32
                          }}
                        >
                          {entry.folderId}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 1.5 }}>
                      <Box
                        component="button"
                        onClick={() => entry.type === 'directory' ? handleNavigate(entry) : isPreviewable(entry) ? handleFullPreview(entry) : null}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          border: 'none',
                          background: 'none',
                          cursor: (entry.type === 'directory' || isPreviewable(entry)) ? 'pointer' : 'default',
                          color: entry.type === 'directory' ? '#ffd54f' : isPreviewable(entry) ? '#00d4ff' : 'rgba(255,255,255,0.9)',
                          fontSize: '0.95rem',
                          fontWeight: (entry.type === 'directory' || isPreviewable(entry)) ? 600 : 400,
                          '&:hover': (entry.type === 'directory' || isPreviewable(entry)) ? {
                            textDecoration: 'underline'
                          } : {},
                          transition: 'all 0.2s ease',
                          textAlign: 'left'
                        }}
                      >
                        {entry.name}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {entry.type === 'directory' ? <FolderIcon sx={{ fontSize: 16, color: '#ffd54f' }} /> : isPreviewable(entry) ? <PhotoIcon sx={{ fontSize: 16, color: '#00d4ff' }} /> : isVid ? <VideoIcon sx={{ fontSize: 16, color: '#ff4081' }} /> : <FileIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />}
                        <Typography
                          variant="body2"
                          sx={{
                            color: entry.type === 'directory' ? '#ffd54f' : 'rgba(255,255,255,0.5)',
                            textTransform: 'capitalize',
                            fontSize: '0.8rem'
                          }}
                        >
                          {entry.type}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace' }}>
                        {entry.type === 'directory' ? '-' : formatBytes(entry.size)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {entry.type === 'file' ? (
                          <>
                            {isPreviewable(entry) && (
                              <Tooltip title="View High Quality">
                                <IconButton size="small" onClick={() => handleFullPreview(entry)} sx={{ color: '#00d4ff', '&:hover': { background: 'rgba(0, 212, 255, 0.1)' } }}>
                                  <ZoomInIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Download file">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadFile(entry)}
                                disabled={downloading}
                                sx={{
                                  color: 'rgba(255,255,255,0.5)',
                                  '&:hover': { color: '#00ff88', background: 'rgba(0, 255, 136, 0.1)' }
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title="Download Folder as ZIP">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadFolder(entry)}
                              disabled={downloading}
                              sx={{
                                color: 'rgba(255,255,255,0.3)',
                                '&:hover': { color: '#ffd54f', background: 'rgba(255, 213, 79, 0.1)' }
                              }}
                            >
                              <CloudDownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Full Preview Dialog */}
      <Dialog
        open={Boolean(fullPreviewEntry)}
        onClose={closeFullPreview}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            background: 'rgba(10, 10, 15, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          p: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PhotoIcon sx={{ color: '#00d4ff' }} />
            <Box>
              <Typography variant="body1" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                {fullPreviewEntry?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                {fullPreviewEntry && formatBytes(fullPreviewEntry.size)} • High Quality Preview
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={closeFullPreview} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ff4444' } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, background: '#000' }}>
          {fullPreviewLoading ? (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress sx={{ color: '#00d4ff', mb: 2 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Optimizing image for preview...</Typography>
            </Box>
          ) : fullPreviewUrl ? (
            <Zoom in={true}>
              <Box
                component="img"
                src={fullPreviewUrl}
                alt="Full Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Zoom>
          ) : null}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', background: 'rgba(255,255,255,0.02)' }}>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={() => handleDownloadFile(fullPreviewEntry)}
            sx={{ 
              borderColor: 'rgba(0, 212, 255, 0.3)', 
              color: '#00d4ff',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { borderColor: '#00d4ff', background: 'rgba(0, 212, 255, 0.05)' }
            }}
          >
            Download Full Quality
          </Button>
        </Box>
      </Dialog>

      {/* Bulk Download Dialog */}
      <Dialog
        open={bulkDialogOpen}
        onClose={() => !bulkDownloading && setBulkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            background: 'rgba(20, 25, 35, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 100px rgba(0, 212, 255, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#fff', 
          fontWeight: 700,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LayersIcon sx={{ color: '#00ff88', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Bulk Download by ID Range
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
            Enter the folder ID range you want to download. Folders are sorted by year-month order.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Start ID"
              type="number"
              value={bulkStartId}
              onChange={(e) => setBulkStartId(e.target.value)}
              disabled={bulkDownloading}
              InputProps={{
                sx: {
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 212, 255, 0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00d4ff' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00ff88' }
                }
              }}
              InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="End ID"
              type="number"
              value={bulkEndId}
              onChange={(e) => setBulkEndId(e.target.value)}
              disabled={bulkDownloading}
              InputProps={{
                sx: {
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 212, 255, 0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00d4ff' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00ff88' }
                }
              }}
              InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
              sx={{ flex: 1 }}
            />
          </Box>
          
          {bulkError && (
            <Alert severity="error" sx={{ mb: 2, background: 'rgba(255, 82, 82, 0.1)', color: '#ff5252' }}>
              {bulkError}
            </Alert>
          )}
          
          {bulkDownloading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress 
                variant="indeterminate"
                sx={{ 
                  borderRadius: '8px',
                  height: 8,
                  background: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #00ff88, #00d4ff)',
                    borderRadius: '8px',
                  }
                }} 
              />
              <Typography variant="caption" sx={{ color: '#00ff88', mt: 1, display: 'block', textAlign: 'center' }}>
                Scanning folders and preparing ZIP...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2, 
          p: 3,
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Button
            onClick={() => setBulkDialogOpen(false)}
            disabled={bulkDownloading}
            sx={{
              color: 'rgba(255,255,255,0.6)',
              textTransform: 'none',
              '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.05)' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkDownload}
            disabled={bulkDownloading || !bulkStartId || !bulkEndId}
            sx={{
              background: 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)',
              borderRadius: '12px',
              px: 4,
              py: 1,
              fontWeight: 700,
              boxShadow: '0 4px 15px rgba(0, 255, 136, 0.3)',
              textTransform: 'none',
              color: '#000',
              '&:hover': {
                background: 'linear-gradient(135deg, #00cc6a 0%, #00aa55 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0, 255, 136, 0.4)',
              },
              '&:disabled': {
                background: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.3)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {bulkDownloading ? 'Preparing...' : 'Download ZIP'}
          </Button>
        </Box>
      </Dialog>

      {/* CSS Animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </Box>
  );
}

export default FileExplorer;
